import React from 'react';
import { Box, Paper, Typography, Chip, Stack } from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import SellIcon from '@mui/icons-material/Sell';
import CommentIcon from '@mui/icons-material/Comment';
import type { PlanTimelineEvent } from '../../../../../models/PlanExecutionTypes';

interface Props {
  events: PlanTimelineEvent[];
}

function eventIcon(type: PlanTimelineEvent['type']) {
  switch (type) {
    case 'PURCHASE':
      return <ShoppingCartIcon fontSize="small" color="primary" />;
    case 'SELL':
      return <SellIcon fontSize="small" color="warning" />;
    default:
      return <CommentIcon fontSize="small" color="action" />;
  }
}

function eventChipColor(type: PlanTimelineEvent['type']): 'primary' | 'warning' | 'default' {
  switch (type) {
    case 'PURCHASE':
      return 'primary';
    case 'SELL':
      return 'warning';
    default:
      return 'default';
  }
}

const PlanExecutionTimeline: React.FC<Props> = ({ events }) => (
  <Paper variant="outlined" sx={{ p: 2 }}>
    <Typography variant="subtitle2" color="primary" gutterBottom>
      Transaction timeline
    </Typography>
    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
      Purchases, partial sells, and plan notes in chronological order (newest first).
    </Typography>
    {events.length === 0 ? (
      <Typography variant="body2" color="text.secondary">
        No transactions yet. Record a purchase to start the timeline.
      </Typography>
    ) : (
      <Stack spacing={0}>
        {events.map((ev, index) => (
          <Box key={ev.id} sx={{ display: 'flex', gap: 2, position: 'relative', pb: 2.5 }}>
            {index < events.length - 1 && (
              <Box
                sx={{
                  position: 'absolute',
                  left: 15,
                  top: 32,
                  bottom: 0,
                  width: 2,
                  bgcolor: 'divider',
                }}
              />
            )}
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                bgcolor: 'action.hover',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                zIndex: 1,
              }}
            >
              {eventIcon(ev.type)}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="subtitle2">{ev.title}</Typography>
                <Chip label={ev.type} size="small" color={eventChipColor(ev.type)} variant="outlined" />
                <Typography variant="caption" color="text.secondary">
                  {ev.date}
                </Typography>
              </Stack>
              {ev.subtitle && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                  {ev.subtitle}
                </Typography>
              )}
              {ev.metrics && ev.metrics.length > 0 && (
                <Stack direction="row" spacing={2} sx={{ mt: 0.75 }} flexWrap="wrap" useFlexGap>
                  {ev.metrics.map((m) => (
                    <Typography key={m.label} variant="caption">
                      <strong>{m.label}:</strong> {m.value}
                    </Typography>
                  ))}
                </Stack>
              )}
              {ev.body?.trim() && (
                <Typography variant="body2" sx={{ mt: 0.75, whiteSpace: 'pre-wrap' }}>
                  {ev.body}
                </Typography>
              )}
            </Box>
          </Box>
        ))}
      </Stack>
    )}
  </Paper>
);

export default PlanExecutionTimeline;
